import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { ErrorState } from '@xing-com/empty-state';
import { GridContainer, GridItem } from '@xing-com/grid';
import { useViewerData } from '@xing-com/hub';

import { recommendationPageToTrackingPageMap } from '../../config';
import type { RecommendationPageType } from '../../config';
import { useRecommendationCards } from '../../hooks/use-recommendation-cards';
import { BackButton } from '../back-button/back-button';
import { CardsModule } from '../cards-module/cards-module';
import { CardsModuleSkeleton } from '../cards-module/cards-module.skeleton';
import { CardsSorting } from '../cards-sorting/cards-sorting';
import { PageInfo } from '../page-info/page-info';

import * as Styled from './page.styles';

type PageProps = {
  type: RecommendationPageType;
};

export const Page: React.FC<PageProps> = ({ type }) => {
  const { formatMessage } = useIntl();
  const { data: viewerData, loading } = useViewerData();
  const { track: trackNwt } = useTracking();
  const { isLoggedIn } = useLoginState();

  const {
    recommendationCardItems,
    isLoading,
    isLoadingMore,
    hasError,
    hasLoadMoreButton,
    refetchRecommendations,
    sortCards,
    loadMore,
  } = useRecommendationCards(type);

  useEffect(() => {
    const userId = isLoggedIn ? { userId: viewerData?.user?.id } : {};

    if (!loading) {
      trackNwt({
        type: 'nwt',
        event: 'viewed_screen',
        application: 'wbm',
        eventSchema: 'basic',
        sentBy: 'content',
        page: `following/recommendations/${recommendationPageToTrackingPageMap[type]}`,
        ...userId,
      });
    }
  }, [loading, trackNwt, type, viewerData?.user?.id, isLoggedIn]);

  return (
    <GridContainer variant="FullSize">
      <GridItem span={12}>
        <Styled.RecommendationPageContainer>
          {isLoggedIn ? <BackButton /> : null}
          <PageInfo type={type} />
          <CardsSorting sortCards={sortCards} isLoading={isLoading} />
          {isLoading ? (
            <CardsModuleSkeleton />
          ) : hasError ? (
            <ErrorState
              headlineText={formatMessage({ id: 'EP_GENERAL_ERROR_TITLE' })}
              bodyCopyText={formatMessage({ id: 'EP_GENERAL_ERROR_TEXT' })}
              buttonText={formatMessage({ id: 'EP_GENERAL_ERROR_BUTTON' })}
              size="medium"
              withBackground
              buttonProps={{
                loading: isLoading,
                onClick: () => refetchRecommendations(),
              }}
            />
          ) : (
            <CardsModule
              cardItems={recommendationCardItems}
              showMoreButton={hasLoadMoreButton}
              loadMore={loadMore}
              isLoadingMore={isLoadingMore}
            />
          )}
        </Styled.RecommendationPageContainer>
      </GridItem>
    </GridContainer>
  );
};
